/**
 * Navigation model for the app shell: which entries each user sees, in what order, and
 * which one is "current" for a given route.
 *
 * Deliberately pure TypeScript (no Vue, Pinia or router imports) so the role/feature gating
 * can be unit-tested in Vitest's node environment. The sidebar and the mobile nav build a
 * `NavContext` from the auth/whatsapp/inbox stores and render whatever `buildNav` returns.
 */

import { campaignChannels, canOpenEngage, type EngageChannel, type EngageContext } from '@/lib/engageGate'

export type NavIcon =
  | 'today'
  | 'pipeline'
  | 'deals'
  | 'hot'
  | 'contacts'
  | 'companies'
  | 'products'
  | 'payments'
  | 'inbox'
  | 'activities'
  | 'outreach'
  | 'whatsapp'
  | 'recordings'
  | 'notes'
  | 'analytics'
  | 'myday'
  | 'cadence'
  | 'settings'
  | 'more'

export interface NavItem {
  to: string
  label: string
  icon: NavIcon
  /**
   * Rendered as the link's `data-help-target`: the anchor the onboarding tour's steps
   * (constants/help/tour-steps.ts) highlight. Not a tooltip.
   */
  help?: string
  /** Path prefix that marks this entry current when it differs from `to`. */
  activePath?: string
  /**
   * Several path prefixes that mark this entry current, for an entry that took over another page's
   * links (Products & payments: `/products` and the old `/payments`). Used instead of `activePath`.
   */
  activePaths?: string[]
  /** Paths under `to` that belong to another entry, so this one is not current there. */
  excludePaths?: string[]
  /** Small count pill, right-aligned in the link — only the Inbox uses it. */
  badge?: number
  /** Paid feature the org can't use right now: shows a lock, still navigates to the upsell. */
  locked?: boolean
}

export interface NavGroup {
  label: string | null
  items: NavItem[]
}

export interface NavContext {
  // `| undefined` so callers can pass `authStore.user?.role` straight through under
  // exactOptionalPropertyTypes.
  role?: string | undefined
  isAdmin: boolean
  isKam: boolean
  isRootUser: boolean
  /** `Organization.whatsapp.enabled` — opens the Inbox and Campaigns to non-admins. */
  whatsappEnabled: boolean
  waUnread: number
  /**
   * Threads waiting for a reply (`GET /inbox/counts` → `all`). Undefined until the counts load
   * (or when they failed): the Inbox badge then shows `waUnread` instead (R32).
   */
  inboxWaiting?: number | undefined
  /** `Organization.recordings.enabled` — paid feature; the entry stays visible but locked. */
  recordingsEnabled: boolean
  /** `Organization.waMessages.enabled !== false` — the WhatsApp campaign channel is locked when off. */
  waMessagesEnabled: boolean
  /** `Organization.emails.enabled !== false` — the email campaign channel is locked when off. */
  emailsEnabled: boolean
}

/** The role-based landing dashboard. Members and readonly users get the sales dashboard,
 *  KAMs the KAM dashboard, everyone else (owner, admins, unknown) the management one. */
export function dashboardPathFor(role?: string): '/dashboard' | '/sales-dashboard' | '/kam-dashboard' {
  if (role === 'member' || role === 'readonly') return '/sales-dashboard'
  if (role === 'kam') return '/kam-dashboard'
  return '/dashboard'
}

function engageContextOf(ctx: NavContext): EngageContext {
  return { isAdmin: ctx.isAdmin, whatsappEnabled: ctx.whatsappEnabled, isReadonly: ctx.role === 'readonly' }
}

/** The Engage pages (Inbox, Campaigns) are open to admins (email) and to non-readonly users whose
 *  org has WhatsApp — the same rule the router guard applies (lib/engageGate.ts). */
export function engageOpen(ctx: NavContext): boolean {
  return canOpenEngage(engageContextOf(ctx))
}

/** The Inbox entry (sidebar and tab bar): the waiting count once loaded, else WhatsApp unread. */
function inboxItem(ctx: NavContext, help?: string): NavItem {
  const item: NavItem = { to: '/inbox', label: 'Inbox', icon: 'inbox', badge: ctx.inboxWaiting ?? ctx.waUnread }
  if (help) item.help = help
  return item
}

/** Campaigns is locked only when every channel this user can see is a locked paid channel, so
 *  one usable channel keeps the entry open (the page upsells the locked one itself). */
function campaignsLocked(ctx: NavContext): boolean {
  const lockedChannel: Record<EngageChannel, boolean> = { email: !ctx.emailsEnabled, whatsapp: !ctx.waMessagesEnabled }
  const channels = campaignChannels(engageContextOf(ctx))
  return channels.length > 0 && channels.every((c) => lockedChannel[c])
}

/** The Inbox entry for this user (the legacy mobile drawer draws it too), or null when they
 *  cannot open the Inbox. */
export function inboxNavItem(ctx: NavContext): NavItem | null {
  return engageOpen(ctx) ? inboxItem(ctx) : null
}

/** An item plus whether this user should see it. Dropped before anything is returned. */
interface Candidate {
  item: NavItem
  show: boolean
}

/**
 * Feature-grouped navigation. Each item is gated by role and/or org feature flags; hidden
 * items are removed and groups left with no items are dropped (so reps never see an empty
 * header). The footer holds the admin-only Settings entry.
 */
export function buildNav(ctx: NavContext): { groups: NavGroup[]; footer: NavItem[] } {
  const groupDefs: { label: string | null; candidates: Candidate[] }[] = [
    {
      label: null,
      candidates: [
        {
          item: { to: dashboardPathFor(ctx.role), label: 'Today', icon: 'today', activePath: '/dashboard' },
          show: true,
        },
      ],
    },
    {
      label: 'Sell',
      candidates: [
        // `/pipeline` is a shortcut with no page: its route guard redirects to the board the user
        // was last on (see lib/pipelineHome.ts). Prefix matching keeps the entry current on
        // `/pipeline`, the `/pipelines` list and every `/pipelines/:id` board.
        { item: { to: '/pipeline', label: 'Pipeline', icon: 'pipeline', help: 'nav-pipelines' }, show: true },
        // Hot deals has its own entry right below, so Deals is not current there.
        { item: { to: '/deals', label: 'Deals', icon: 'deals', help: 'nav-deals', excludePaths: ['/deals/hot'] }, show: true },
        // No count badge (R31): the page ranks deals by buying signals; a count would be noise.
        { item: { to: '/deals/hot', label: 'Hot deals', icon: 'hot', help: 'nav-hot-deals' }, show: true },
        { item: { to: '/contacts', label: 'Contacts', icon: 'contacts', help: 'nav-contacts' }, show: true },
        { item: { to: '/companies', label: 'Companies', icon: 'companies' }, show: true },
        // One page for the catalog and the payments (D15). Everyone gets the entry: the payments
        // section inside is gated (admin or KAM, not root — productsPageModel.canSeePayments).
        // The old /payments links redirect into it, so the entry stays current there too.
        {
          item: {
            to: '/products',
            label: 'Products & payments',
            icon: 'products',
            help: 'nav-products',
            activePaths: ['/products', '/payments'],
          },
          show: true,
        },
      ],
    },
    {
      label: 'Engage',
      candidates: [
        // One Inbox and one Campaigns page for both channels (phase 6). Each lists email for
        // admins and WhatsApp when the org has it on; the backend scopes non-admins to their own
        // contacts' threads and the campaigns they created, so a WhatsApp member needs both.
        { item: inboxItem(ctx, 'nav-inbox'), show: engageOpen(ctx) },
        { item: { to: '/activities', label: 'Activities', icon: 'activities', help: 'nav-activities' }, show: true },
        {
          item: {
            to: '/campaigns',
            label: 'Campaigns',
            icon: 'outreach',
            help: 'nav-campaigns',
            locked: campaignsLocked(ctx),
          },
          show: engageOpen(ctx),
        },
        // Call recording is a paid feature: the entry stays VISIBLE when unentitled (locked,
        // linking to the upsell) because hiding it teaches the user nothing and sells nothing.
        {
          item: {
            to: '/recordings',
            label: 'Recordings',
            icon: 'recordings',
            help: 'nav-recordings',
            locked: !ctx.recordingsEnabled,
          },
          show: ctx.isAdmin,
        },
        { item: { to: '/notes', label: 'Notes', icon: 'notes', help: 'nav-notes' }, show: ctx.isAdmin },
      ],
    },
    {
      label: 'Insights',
      candidates: [
        {
          item: {
            to: '/analytics/sales',
            label: 'Analytics',
            icon: 'analytics',
            help: 'nav-analytics',
            activePath: '/analytics',
          },
          show: ctx.isAdmin,
        },
        // Non-admins get the one Analytics page they can open — their own day. Not readonly
        // accounts: the timeline API answers them with a 403, so the entry would lead nowhere.
        {
          item: {
            to: '/analytics/timeline',
            label: 'My day',
            icon: 'myday',
            help: 'nav-my-day',
            activePath: '/analytics/timeline',
          },
          show: !ctx.isAdmin && ctx.role !== 'readonly',
        },
        { item: { to: '/deal-cadence', label: 'Deal cadence', icon: 'cadence', help: 'nav-deal-cadence' }, show: true },
      ],
    },
  ]

  const groups: NavGroup[] = groupDefs
    .map((g) => ({ label: g.label, items: g.candidates.filter((c) => c.show).map((c) => c.item) }))
    .filter((g) => g.items.length > 0)

  // Settings visible to admin+ (admins land on API Keys for recorder agent provisioning;
  // owner-only sub-pages stay gated at the route + sub-nav level).
  const footer: NavItem[] = ctx.isAdmin ? [{ to: '/settings', label: 'Settings', icon: 'settings' }] : []

  return { groups, footer }
}

/**
 * The mobile bottom tab bar: exactly five fixed slots, the same for every role. The pages a
 * role can't open aren't hidden here — the fifth tab, More, leads to the full menu.
 *
 * The third slot is the Inbox when this user can open it (admins, and non-readonly users in a
 * WhatsApp org), with the same count as the sidebar (the component decides when to draw the pill),
 * and Activities otherwise, so the bar never has a dead or missing slot. The Deals tab stays current
 * on Hot deals, which has no tab of its own.
 */
export function buildMobileTabs(ctx: NavContext): NavItem[] {
  const third: NavItem = inboxNavItem(ctx) ?? { to: '/activities', label: 'Activities', icon: 'activities' }
  return [
    { to: dashboardPathFor(ctx.role), label: 'Today', icon: 'today', activePath: '/dashboard' },
    { to: '/deals', label: 'Deals', icon: 'deals' },
    third,
    { to: '/contacts', label: 'Contacts', icon: 'contacts' },
    { to: '/more', label: 'More', icon: 'more' },
  ]
}

/**
 * Whether `item` is the current entry for `route`. Matches on `item.activePaths`, else
 * `item.activePath || item.to`, by path prefix, minus the item's `excludePaths`, with two special
 * cases: Today and Campaigns.
 */
export function isNavItemActive(
  item: NavItem,
  route: { path: string; name?: string | symbol | null | undefined },
): boolean {
  const path = item.activePath || item.to
  if (path === '/dashboard') {
    // Every role dashboard lights up Today, whichever one this user's `to` points at.
    return (
      route.path === '/dashboard' ||
      route.path === '/sales-dashboard' ||
      route.path === '/kam-dashboard' ||
      route.name === 'Dashboard' ||
      route.name === 'SalesDashboard' ||
      route.name === 'KamDashboard'
    )
  }
  if (item.excludePaths?.some((p) => route.path.startsWith(p))) return false
  if (item.activePaths) return item.activePaths.some((p) => route.path.startsWith(p))
  // The campaign builders keep their per-channel paths; they belong to Campaigns.
  if (path === '/campaigns') {
    return (
      route.path.startsWith('/campaigns') ||
      route.path.startsWith('/outreach/campaigns') ||
      route.path.startsWith('/whatsapp/campaigns')
    )
  }
  return route.path.startsWith(path)
}
